import { useHotkeys } from 'react-hotkeys-hook';
import { useQuery } from 'react-query';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonAPI, PersonHelpers } from '@frontend/api-person';
import { getUser } from '@frontend/auth-helpers';
import { useHasConnectedSoftphone } from '@frontend/phone-config';
import { useCallPopStateSync } from '@frontend/pop';
import { useAppScopeStore } from '@frontend/scope';
import { CallPopTypes } from '@frontend/types';

export const useCallPopDemo = () => {
  const { addNotification } = useCallPopStateSync();
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const locationId = selectedLocationIds[0];
  const location = accessibleLocationData[locationId];
  const hasSoftphone = useHasConnectedSoftphone();
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const user = getUser();

  const { data } = useQuery({
    queryKey: [locationId, 'callpop-demo-contact', demoSourceIds],
    queryFn: () => {
      return PersonAPI.getPersons(1, 0, `${user?.firstName} ${user?.lastName}`.trim(), '', locationId, demoSourceIds);
    },
    enabled: !!locationId,
  });

  const { data: fallbackData } = useQuery({
    queryKey: [locationId, 'callpop-demo-contact-fallback', demoSourceIds],
    queryFn: () => {
      return PersonAPI.getPersons(1, 0, `Kristy Abbott`, '', locationId, demoSourceIds);
    },
    enabled: !!locationId,
  });

  useHotkeys(
    'ctrl+alt+p',
    () => {
      const person = data?.rows[0] ?? fallbackData?.rows[0];

      if (!person || !location) {
        return;
      }

      let notification: CallPopTypes.Notification = {
        id: 'demo',
        timestamp: new Date().toString(),
        payload: {
          type: 'default',
          autoShowCallerProfile: true,
          callerContext: 'billing',
          contacts: [
            {
              callerName: PersonHelpers.getFullName(person),
              callerNumber: person.MobilePhone,
              // We will display the location name of the first location in the selected list
              recipientLocationName: location?.name ?? '',
              personId: person.PersonID,
              patientId: person.PMID,
              householdId: person.PMID,
              birthdate: new Date(person.Birthdate).getTime(),
              gender: person.Gender,
              source: person.SourceName,
              matchedLocationId: location?.id,
              pets: [],
            },
          ],
          recipientLocationName: person.SourceName,
          headOfHousehold: {
            head_of_household_id: '',
            head_of_household_person_id: '',
          },
          officeNumberName: '',
          officeNumber: '',
        },
      };

      /**
       * Why didn't I just do a ternary on the type field where we define the notification?
       * Because typescript. ✨
       */
      if (hasSoftphone) {
        notification = {
          ...notification,
          payload: {
            ...notification.payload,
            type: 'softphone',
          },
        };
      }

      addNotification(notification);
    },
    { enabled: !!data && location?.opsType === 'DEMO' },
    [data, locationId, hasSoftphone, fallbackData, demoSourceIds]
  );
};
